import { Tooltip } from '@mui/material';
import { Category } from 'TYPES/api.body';
import { AddCart, LikeIcon, LogoIcon } from 'assets/Icons';
import { ProductQtyCounter } from 'components/atoms';
import QUERY_KEYS from 'network/config/queryKeys';
import {
  addProductToCart,
  likeProduct,
  removeProductFromCart,
  unlikeProduct,
  updateCartItemQty
} from 'network/mutations/products';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { useRecoilState, useRecoilValue } from 'recoil';
import { IWishList, wishListState } from 'store/atoms/productDetail';
import { profileAtom } from 'store/atoms/profileAtom';
import { userRoleAtom } from 'store/atoms/userAtom';
import { removeItemAtIndex } from 'utils/arrayHelpers';
import { formatCurrency } from 'utils/currencyFormater';
import { useUpdatedCart } from '../Cart/hooks';
import { GoodsCon } from './style';
import { performAddProductToCart, performUpdateCartItemQty } from './utils';

type CustomerType = 'REGULAR' | 'BUSINESS';

interface IProductCard {
  setOnSeeGoodsDetails: () => void;
  seller: {
    seller_logo: string;
    businessName: string;
    id: number;
  };
  productId: number;
  productName?: string;
  name?: string;
  price: number;
  quantityAvailable: number;
  minimumOrderQuantity: number;
  bulkOrderPrice: number;
  barCode: string;
  image?: string;
  productImage1: string | string[];
  productImage2?: string | string[];
  productImage3?: string | string[];
  productImage4?: string | string[];
  likes?: number;
  status: string;
  description: string;
  category?: Category;
}

// Debounce utility function
const debounce = <T extends (...args: any[]) => any>(func: T, wait: number): ((...args: Parameters<T>) => void) => {
  let timeout: NodeJS.Timeout;
  return (...args: Parameters<T>) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
};

// Retry utility for optimistic locking errors
const retryOnOptimisticLock = async <T,>(
  operation: () => Promise<T>,
  maxRetries: number = 3,
  baseDelay: number = 100
): Promise<T> => {
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      return await operation();
    } catch (error: any) {
      const isOptimisticLockError =
        error?.response?.data?.message?.includes('ObjectOptimisticLockingFailureException') ||
        error?.message?.includes('ObjectOptimisticLockingFailureException');

      if (isOptimisticLockError && attempt < maxRetries) {
        // Exponential backoff
        const delay = baseDelay * Math.pow(2, attempt - 1);
        await new Promise(resolve => setTimeout(resolve, delay));
        continue;
      }
      throw error;
    }
  }
  throw new Error('Max retries exceeded');
};

export const ProductCard = ({ setOnSeeGoodsDetails, ...rest }: IProductCard) => {
  const route = useNavigate();
  const queryClient = useQueryClient();
  const userRole = useRecoilValue(userRoleAtom) as CustomerType;
  const { mutateAsync: addMutate } = useMutation({ mutationFn: addProductToCart });
  const { mutateAsync: updateMutate } = useMutation({ mutationFn: updateCartItemQty });
  const { mutateAsync: removeMutate } = useMutation({ mutationFn: removeProductFromCart });
  const serverCart = useUpdatedCart();

  const [result, setResult] = useState<{ orderId: number | null; productFound: boolean }>({
    orderId: null,
    productFound: false
  });
  const [quantity, setQuantity] = useState(1);
  const [isUpdating, setIsUpdating] = useState(false);

  // const { productId: id, productName: name, price: amount, description: shortDSC, productImage1: img } = rest;
  const { productId: id, productName: name, price: amount, description: shortDSC, productImage1, image } = rest;
  const img = image || productImage1;

  useEffect(() => {
    if (id !== undefined && serverCart?.data) {
      const found = findProductInCart(id, serverCart?.data);
      setResult(found);
      if (found.productFound) {
        const product = serverCart.data.orders
          .flatMap(order => order.products)
          .find(product => product.productId === id);
        if (product) {
          setQuantity(product.quantity);
        }
      }
    }
  }, [serverCart?.data, id]);

  function findProductInCart(productId: number, cart: any) {
    if (!cart || !Array.isArray(cart.orders)) {
      return { orderId: null, productFound: false };
    }
    for (const order of cart.orders) {
      for (const product of order.products) {
        if (product.productId === productId) {
          return { orderId: order.id, productFound: true };
        }
      }
    }
    return { orderId: null, productFound: false };
  }

  const profile = useRecoilValue(profileAtom);
  const [wishList, setWishList] = useRecoilState(wishListState);
  const wishitemIndex = wishList.findIndex(item => item.id === id);
  const isItemInWishlist = wishitemIndex >= 0 || (rest.likes ?? 0) > 0;

  const { mutateAsync: likeProductMutate, isLoading: likeLoading } = useMutation({
    mutationFn: likeProduct,
    onError: err => {
      setWishList(removeItemAtIndex(wishList, wishitemIndex));
    }
  });

  const { mutateAsync: unlikeProductMutate, isLoading: unLikeLoading } = useMutation({
    mutationFn: unlikeProduct,
    onError: error => {
      setWishList(c => [
        ...c,
        {
          id,
          amount,
          img: Array.isArray(img) ? img[0] : img,
          name: rest.name || rest.productName || 'Unnamed Product', // <-- always a string
          isLiked: false,
          shortDSC,
          imgs: Array.isArray(img) ? img : [img],
          atrs: []
        }
      ]);
    }
  });

  // Debounced quantity update function
  const debouncedQuantityUpdate = useMemo(
    () =>
      debounce(async (newQuantity: number) => {
        if (isUpdating) return;

        setIsUpdating(true);
        try {
          await retryOnOptimisticLock(async () => {
            if (newQuantity < 1) {
              // Remove item completely when quantity goes below 1
              await performUpdateCartItemQty(
                0,
                { productId: id },
                result?.orderId || 0,
                updateMutate,
                removeMutate,
                userRole,
                queryClient
              );
            } else {
              await performUpdateCartItemQty(
                newQuantity,
                { productId: id },
                result?.orderId || 0,
                updateMutate,
                removeMutate,
                userRole,
                queryClient
              );
            }
          });
        } catch (error) {
          console.error('Failed to update quantity after retries:', error);
          // Revert to previous quantity on failure
          if (serverCart?.data) {
            const product = serverCart.data.orders
              .flatMap(order => order.products)
              .find(product => product.productId === id);
            if (product) {
              setQuantity(product.quantity);
            }
          }
        } finally {
          setIsUpdating(false);
        }
      }, 300),
    [id, result?.orderId, updateMutate, removeMutate, userRole, queryClient, isUpdating, serverCart?.data]
  );

  const handleQuantityChange = useCallback(
    (newQuantity: number) => {
      // Update UI immediately for responsiveness
      setQuantity(newQuantity);

      // Debounced API call
      debouncedQuantityUpdate(newQuantity);
    },
    [debouncedQuantityUpdate]
  );

  // ...existing code...
  const toggleCartItem: React.MouseEventHandler<HTMLButtonElement> = async e => {
    e.stopPropagation();
    if (profile === undefined) {
      route('/login');
      return;
    }

    if (userRole === 'BUSINESS') {
      setOnSeeGoodsDetails();
    } else {
      await performAddProductToCart(
        serverCart?.data,
        id,
        1,
        result?.orderId,
        addMutate,
        updateMutate,
        removeMutate,
        queryClient,
        'REGULAR'
      );
      // Silent refresh after add
      queryClient.invalidateQueries(QUERY_KEYS.CART);
    }
  };
  // ...existing code...

  const toggleLikeProduct: React.MouseEventHandler = e => {
    e.stopPropagation();
    if (profile === undefined) {
      route('/login');
      return;
    }
    let newArray: IWishList;
    if (isItemInWishlist) {
      unlikeProductMutate(id).then(() => queryClient.invalidateQueries([QUERY_KEYS.FAVOURITE_PRODUCTS]));
      newArray = wishList.filter(item => item.id !== id);
    } else {
      likeProductMutate(id).then(() => queryClient.invalidateQueries([QUERY_KEYS.FAVOURITE_PRODUCTS]));
      newArray = [
        ...wishList,
        {
          id,
          amount,
          img: Array.isArray(img) ? img[0] : img,
          name: (rest.name ?? rest.productName ?? 'Unnamed Product'), // <-- this ensures string
          isLiked: false,
          shortDSC,
          imgs: Array.isArray(img) ? img : [img],
          atrs: []
        }
      ];
    }
    setWishList(newArray);
  };

  const imgSrc = Array.isArray(img) ? img[0] : img;
  const isOutOfStock = rest.quantityAvailable === 0;

  // Prevent click events when out of stock
  const handleCardClick = (clickHandler: () => void) => {
    if (!isOutOfStock) {
      clickHandler();
    }
  };

  return (
    <GoodsCon $isLiked={isItemInWishlist} $isOutOfStock={isOutOfStock}>
      {/* Image section - clickable for product details */}
      <div className="imageCon" onClick={() => handleCardClick(setOnSeeGoodsDetails)}>
        {img != null ? <img id={name} src={imgSrc} alt={name} /> : <LogoIcon />}
      </div>

      {/* Product details */}
      <div className="goodsDetails">
        {/* Product name and price */}
        <div className="info">
          <Tooltip title={rest.name || rest.productName || 'Unnamed Product'}>
            <h3 onClick={() => handleCardClick(setOnSeeGoodsDetails)}>
              {rest.name || rest.productName || 'Unnamed Product'}
            </h3>
          </Tooltip>
          <h4>{formatCurrency(amount)}</h4>
        </div>

        {/* Quantity Available */}
        {/* <div className="quantity-info">
          <span className={`quantity-badge ${rest.quantityAvailable > 10
            ? 'in-stock'
            : rest.quantityAvailable > 0
              ? 'low-stock'
              : 'out-of-stock'
            }`}>
            {rest.quantityAvailable > 0 ? `${rest.quantityAvailable} left` : 'Out of stock'}
          </span>
        </div> */}

        {/* Quantity Available - Only show when out of stock */}
        {/* Quantity Available - ALWAYS show this div to reserve space */}
        <div className="quantity-info">
          {rest.quantityAvailable === 0 && (
            <span className="quantity-badge out-of-stock">
              Out of stock
            </span>
          )}
        </div>

        {/* Description and bottom actions */}
        <div className="info">
          {shortDSC ? (
            <p className="line-clamp-2 text-sm text-gray-600" onClick={setOnSeeGoodsDetails}>
              {shortDSC.replace(/<[^>]*>/g, '')}
            </p>
          ) : null}

          {/* Bottom row with like button and cart action */}
          <div className="bottom-actions">
            <button
              className="like-button"
              disabled={likeLoading || unLikeLoading}
              onClick={toggleLikeProduct}
              type="button">
              <LikeIcon />
            </button>

            {/* Only show cart controls if product is in stock */}
            {rest.quantityAvailable > 0 && (
              <>
                {result?.productFound ? (
                  <ProductQtyCounter
                    value={quantity}
                    setValue={handleQuantityChange}
                    customerType={userRole}
                    max={rest.quantityAvailable}
                    minimumOrderQuantity={rest.minimumOrderQuantity}
                    disabled={isUpdating}
                  />
                ) : (
                  <button className="outline-none" onClick={toggleCartItem}>
                    <div className="addScale">
                      <AddCart />
                    </div>
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </GoodsCon>
  );
};
